import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { toast } from 'react-toastify';

import { useAddWalletMutation, useUpdateUsernameMutation } from '@query';

import { DeserializedUser, RequestSignResult, WalletType } from '@api';

import { PasskeysRound } from '@uikit/icons/PassKeys';
import { WalletConnect } from '@uikit/icons/WalletConnect';
import { Spinner } from '@uikit/index';

import { namedWeb3Errors } from '@shared/constants';
import displayErrorToast, { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { getErrorMessage } from '@shared/helpers/error';
import { suppressAccountChangeLogout } from '@shared/hooks/web3/useListenToAccountChange';
import useWeb3, { RequestSignProps } from '@shared/hooks/web3/useWeb3';

import { CopyButton } from '@ui/uikit/components/common/CopyButton';
import { DialogButtonsWrapper } from '@ui/uikit/components/common/Dialog';
import { FormInput } from '@ui/uikit/components/common/FormInput';
import { cn } from '@ui/uikit/utils';

import { ProfileEmailField } from './ProfileEmailField';
import { WalletConnectType } from './WalletsList/model/walletsConfig';
import { WalletsList } from './WalletsList/WalletsList';

type Props = {
  form: UseFormReturn<{
    username: string;
    email: string;
  }>;
  onSubmit: () => void;
  hideInputFields?: boolean;
  disabled?: boolean;
  user: DeserializedUser;
  showEmailInputOnly?: boolean;
};

export const CompleteYourMagicIdForm = ({
  form,
  onSubmit,
  hideInputFields,
  user,
  showEmailInputOnly,
}: Props) => {
  const [walletConnectType, setWalletConnectType] = useState<WalletType | undefined>();

  const { requestSign, isLoading, setIsLoading } = useWeb3();
  const { mutateAsync: addWallet, isPending: isAdding } = useAddWalletMutation();
  const isDisabled = isLoading || isAdding;

  const { handleSubmit, control, setError, reset } = form;
  const { mutateAsync: updateUsername } = useUpdateUsernameMutation();

  const onSubmitHandler = handleSubmit(async (data) => {
    if (data.username && !user?.attributes.name) {
      try {
        await updateUsername(data.username);

        reset(data);
      } catch (error) {
        setError('username', {
          type: 'backend',
          message: getErrorMessage(error),
        });

        return;
      }
    }

    onSubmit();
  });

  const resetState = () => {
    setIsLoading(false);
  };

  const handleSignResult = async (signResult?: RequestSignResult) => {
    if (!signResult) {
      return setIsLoading(false);
    }

    try {
      const { address, walletType, network } = signResult;

      await addWallet({ network, wallet: walletType, address });

      toast.success('Wallet successfully assigned');
      resetState();
    } catch (error) {
      setIsLoading(false);

      const normalizedError: Error = normalizeError(error);

      if (normalizedError.message === namedWeb3Errors.SIGNATURE_VERIFICATION_HAS_FAILED) {
        throw normalizedError;
      }

      displayErrorToast({ error });
    }
  };

  const connectWallet = async (connectType: WalletType) => {
    try {
      setIsLoading(true);
      setWalletConnectType(connectType);

      const signProps: RequestSignProps = {
        walletType: connectType,
        signType: 'SIWE',
        message: 'Confirm adding a wallet',
      };

      suppressAccountChangeLogout();

      const signResult = await requestSign(signProps);

      await handleSignResult(signResult);
    } catch (error) {
      resetState();
      displayWalletErrorToast({ error });
    }
  };

  const hasWallets = Boolean(user?.wallets.length);
  const evmWallets =
    user?.wallets.filter((wallet) => wallet.attributes.network !== WalletConnectType.TON) || [];

  return (
    <form className="flex flex-col w-full" onSubmit={onSubmitHandler}>
      {!hideInputFields && (
        <>
          {!showEmailInputOnly && (
            <Controller
              control={control}
              name="username"
              render={({ field: { ref, onChange, value, ...field }, fieldState }) => {
                return (
                  <FormInput
                    inputRef={ref}
                    data-qa="hot-offer-username-input"
                    onValueChange={onChange}
                    value={value}
                    label="Username"
                    className="mb-8"
                    isError={!!fieldState.error}
                    hint={fieldState.error?.message}
                    disabled={Boolean(user?.attributes.name)}
                    afterInput={
                      <CopyButton
                        data-skeleton-target="base"
                        value={value}
                        className="msq-btn msq-btn-icon-lg msq-btn-secondary"
                      />
                    }
                    {...field}
                  />
                );
              }}
            />
          )}
          <ProfileEmailField
            className={cn({
              'mb-0': showEmailInputOnly,
              'mb-8': !showEmailInputOnly,
            })}
          />
        </>
      )}

      {user && !showEmailInputOnly ? (
        <div>
          <p className="flex gap-1 mb-2 text-caption-sm-strong-d">
            {hasWallets ? 'Permanent EVM Wallet' : 'EVM Wallet'}
          </p>
          {hasWallets ? (
            <WalletsList wallets={evmWallets} withConnectWallet={false} withActionButtons={false} />
          ) : (
            <DialogButtonsWrapper className="xs-msq:mt-2 flex-col sm-msq:!flex-row">
              <button
                disabled={isDisabled}
                onClick={() => connectWallet(WalletConnectType.EVM)}
                className="msq-btn msq-btn-lg msq-btn-secondary w-full md-msq:w-[116px]"
              >
                {isLoading && walletConnectType === WalletConnectType.EVM ? (
                  <Spinner size="sm" color="disabled" />
                ) : (
                  <>
                    <WalletConnect className="icon-xs mr-2" />
                    <span>Wallet Connect</span>
                  </>
                )}
              </button>
              <button
                disabled={isDisabled}
                onClick={() => connectWallet(WalletConnectType.PASSKEY)}
                className="msq-btn msq-btn-lg msq-btn-secondary w-full md-msq:w-[116px]"
              >
                {isLoading && walletConnectType === WalletConnectType.PASSKEY ? (
                  <Spinner size="sm" color="disabled" />
                ) : (
                  <>
                    <PasskeysRound className="icon-xs mr-2" />
                    <span>Create Wallet</span>
                  </>
                )}
              </button>
            </DialogButtonsWrapper>
          )}
        </div>
      ) : null}
    </form>
  );
};
